import {
  ActivityFilterKey,
  FeatureName,
  useActivityListFilter,
  useCurrentAccount,
  useCurrentWorkspace,
  useCustomQuery,
  useGrippContext,
  useIsFeatureEnabled,
} from '@gripp/shared-logic';
import {
  CampaignAggregatedActivityData,
  ColumnData,
  IntegrationDigestRow,
  ListCountProvider,
  PanelShell,
} from '@gripp/shared-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { Activities as ActivitiesOld } from './activities-old';
import { ActivityNew } from './activities-new';
import { ActivityFilters } from './activityFilters';
import { ActivityFoldOneColumn } from './activityFoldOneColumn';

type ActivitiesLocationState = {
  skipAutoSelect?: boolean;
};

type ActivityRow = {
  id: string;
};

/**
 * GraphQL query for the campaign-aggregated activity feed (PR 4 / A1).
 *
 * Nested under `campaign { campaignAggregatedActivities }` — graphback's
 * factory hooks don't auto-unwrap nested namespaces, so we use
 * `useCustomQuery` and read `data.campaign.campaignAggregatedActivities`
 * directly. See AGENTS.md "Graphback-nested queries don't auto-unwrap".
 */
const CAMPAIGN_AGGREGATED_ACTIVITIES = `
  query CampaignAggregatedActivities(
    $filter: CampaignAggregatedActivitiesFilter!
  ) {
    campaign {
      campaignAggregatedActivities(filter: $filter) {
        count
        items {
          campaignId
          title
          oemWorkspaceBrand
          assetCount
          replyCount
          latestActivityAt
          firstMediaThumbnailUrl
          latestMessagePreview
        }
      }
    }
  }
`;

type CampaignAggregatedActivitiesResponse = {
  campaign?: {
    campaignAggregatedActivities?: {
      count?: number;
      items?: CampaignAggregatedActivityData[];
    };
  };
};

/**
 * GraphQL query for the integration sync digest feed card (GeoTab, GRIPP
 * #2887). Nested under `integration { syncDigests }`, so — like the campaign
 * aggregated query above — `useCustomQuery` does NOT auto-unwrap the namespace;
 * consumers read `data.integration.syncDigests` directly.
 *
 * Contract is frozen by the `integration.syncDigests` resolver (gripp-fsd.2):
 * the `{ count, items }` wrapper is the envelope, the row fields are the
 * frontend contract. `assets` carries the per-asset breakdown the expand-in-
 * place list renders.
 */
const INTEGRATION_SYNC_DIGESTS = `
  query SyncDigests($filter: SyncDigestsFilter!) {
    integration {
      syncDigests(filter: $filter) {
        count
        items {
          digestKey
          provider
          day
          assetsSynced
          readingsWritten
          issuesOpened
          issuesUpdated
          lastSyncAt
          lastRunStatus
          assets {
            assetId
            assetName
            readingsWritten
            issueCount
          }
        }
      }
    }
  }
`;

type IntegrationSyncDigestsResponse = {
  integration?: {
    syncDigests?: {
      count?: number;
      items?: IntegrationDigestRow[];
    };
  };
};

export const Activities = () => {
  const isCropCircleEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );
  const location = useLocation();

  if (isCropCircleEnabled) {
    return <ActivitiesBase />;
  }

  if (location.pathname.replace(/\/$/, '') === '/activities/filters') {
    return <Navigate to="/activities" replace />;
  }

  if (location.pathname.replace(/\/$/, '') === '/activities/new') {
    return <ActivityNew />;
  }

  return <ActivitiesOld />;
};


const ActivitiesBase = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logger } = useGrippContext();
  const { issueId, digestKey: rawDigestKey } = useParams();
  const selectedDigestKey = rawDigestKey
    ? decodeURIComponent(rawDigestKey)
    : undefined;
  const [count, setCount] = useState<number>();
  const skipAutoSelect = (location.state as ActivitiesLocationState | null)
    ?.skipAutoSelect;
  const isActivityRoot = location.pathname.replace(/\/$/, '') === '/activities';

  const workspace = useCurrentWorkspace();
  const { account } = useCurrentAccount();
  const { data: activityFilter } = useActivityListFilter(
    ActivityFilterKey.Activity
  );

  // Originally the resolver 403'd for OEM workspaces, so we mirrored that gate
  // here. While multi-workspace assets are still missing the resolver guard is
  // temporarily relaxed so OEM users can self-test the aggregated card against
  // their own workspace's assets — match that here. Re-add the OEM exclusion
  // when the resolver guard goes back.  Still guard `workspace?.id` — it's
  // undefined on first render (see AGENTS.md).
  //
  // Per ActivityFilterModel convention, the Campaigns toggle is OFF when the
  // marker array contains 'CAMPAIGN'. Hide the aggregated campaign rows when
  // the toggle is OFF — `excludeActivityTypes` in getActivityFilter already
  // hides inline CAMPAIGN issues from the issue list unconditionally.
  const campaignsToggleOff = (activityFilter?.activityTypes ?? []).includes('CAMPAIGN');
  // The aggregated card requires `campaign.read` (resolver enforces it). Viewers
  // without it (e.g. Restricted) get the campaign issues inline instead — see
  // getActivityFilter — so skip the query to avoid a guaranteed FORBIDDEN.
  //
  // Global admins (`*.*`) also get campaigns inline rather than as the card:
  // their feed is cross-workspace, but the card is single-workspace
  // (`viewingWorkspaceId`) and would miss campaigns from every other workspace.
  // Keep this in lockstep with `surfaceCampaignsAsCards` in the shared-ui
  // activityList, which suppresses the inline CAMPAIGN rows for the same set.
  const surfaceCampaignsAsCards =
    (account?.hasPermission('campaign.read') ?? false) &&
    !(account?.isGlobalAdmin() ?? false);
  const aggregatedQueryEnabled =
    !!workspace?.id && !campaignsToggleOff && surfaceCampaignsAsCards;

  const { data: campaignsAggregatedData } =
    useCustomQuery({
      query: CAMPAIGN_AGGREGATED_ACTIVITIES,
      variables: {
        filter: {
          viewingWorkspaceId: workspace?.id ?? '',
          startDate: activityFilter?.startDate || undefined,
          endDate: activityFilter?.endDate || undefined,
        },
      },
      cacheKey: [
        'campaignAggregatedActivities',
        workspace?.id ?? '',
        activityFilter?.startDate ?? '',
        activityFilter?.endDate ?? '',
        campaignsToggleOff ? 'off' : 'on',
      ],
      options: { enabled: aggregatedQueryEnabled },
    }) as { data?: CampaignAggregatedActivitiesResponse };

  const campaignRows: CampaignAggregatedActivityData[] = useMemo(() => {
    const items =
      campaignsAggregatedData?.campaign?.campaignAggregatedActivities?.items ??
      [];
    // Sort newest first by `latestActivityAt`; rows without a timestamp
    // (no replies yet) fall to the end.
    return [...items].sort((a, b) => {
      const aTs = a.latestActivityAt
        ? new Date(a.latestActivityAt).getTime()
        : 0;
      const bTs = b.latestActivityAt
        ? new Date(b.latestActivityAt).getTime()
        : 0;
      return bTs - aTs;
    });
  }, [campaignsAggregatedData]);

  const campaignRowCount = campaignRows.length;

  // Integration sync digest card (GeoTab, GRIPP #2887). Single-workspace, feed-
  // level (`activity.read`), gated on a defined workspace id. The card never
  // appears on the per-asset feed (no `assetId` here — that lives in a separate
  // route component), so no asset gate is needed at this layer; the section
  // itself also guards on `!assetId` for defense in depth.
  const digestQueryEnabled = !!workspace?.id;

  const {
    data: digestData,
    isError: digestIsError,
    error: digestError,
  } = useCustomQuery({
    query: INTEGRATION_SYNC_DIGESTS,
    variables: {
      filter: {
        workspaceId: workspace?.id ?? '',
        startDate: activityFilter?.startDate || undefined,
        endDate: activityFilter?.endDate || undefined,
      },
    },
    cacheKey: [
      'integrationSyncDigests',
      workspace?.id ?? '',
      activityFilter?.startDate ?? '',
      activityFilter?.endDate ?? '',
    ],
    options: { enabled: digestQueryEnabled },
  }) as {
    data?: IntegrationSyncDigestsResponse;
    isError: boolean;
    error: unknown;
  };

  // Feed must never break on a digest failure. On error we render nothing and
  // log via the existing logger — the digest card is supplementary; the issue
  // list below it is the primary surface and is fetched independently.
  useEffect(() => {
    if (digestIsError) {
      logger.error(
        `Integration sync digest query failed; hiding digest card. ${String(
          digestError
        )}`
      );
    }
  }, [digestIsError, digestError, logger]);

  const digestRows: IntegrationDigestRow[] = useMemo(() => {
    if (digestIsError) {
      return [];
    }
    const items = digestData?.integration?.syncDigests?.items ?? [];
    // Newest day first.
    return [...items].sort((a, b) => {
      const aTs = a.lastSyncAt ? new Date(a.lastSyncAt).getTime() : 0;
      const bTs = b.lastSyncAt ? new Date(b.lastSyncAt).getTime() : 0;
      return bTs - aTs;
    });
  }, [digestData, digestIsError]);

  const onIssueSelected = useCallback(
    (id: string) => {
      navigate(`/activities/${id}`);
    },
    [navigate]
  );

  // First-row auto-select: prefer the most recent campaign aggregated row
  // when present (matches sort order). Falls back to the first Issue row
  // returned by `findIssues`, preserving the legacy behavior.
  const handleDataLoaded = useCallback(
    (data: ActivityRow[]) => {
      if (issueId || skipAutoSelect || !isActivityRoot) return;
      if (campaignRowCount > 0) {
        // Campaign aggregated rows are not yet navigable for Dealers (see
        // activityList.tsx CampaignRowsSection note).  Skip auto-select
        // when they lead the list so the right column stays at "no issue
        // selected" instead of auto-opening an Issue from below the fold.
        return;
      }
      const firstIssueId = data?.[0]?.id;
      if (firstIssueId) {
        onIssueSelected(firstIssueId);
      }
    },
    [
      campaignRowCount,
      isActivityRoot,
      issueId,
      onIssueSelected,
      skipAutoSelect,
    ]
  );

  const handleIssueCountChange = useCallback(
    (issueCount: number) => {
      setCount(issueCount + campaignRowCount);
    },
    [campaignRowCount]
  );

  const handleFilterChange = useCallback(() => {
    navigate('/activities/filters', { state: { skipAutoSelect: true } });
  }, [navigate]);

  const leftColumn: ColumnData = {
    title: 'activity.menu.title',
    count,
    mainComponent: (
      <ActivityFoldOneColumn
        onIssueSelected={onIssueSelected}
        showAssetName={true}
        selectedIssue={issueId}
        onDataLoaded={handleDataLoaded}
        onActivityCountChange={handleIssueCountChange}
        hideFilterBar={true}
        campaignRows={campaignRows}
        digestRows={digestRows}
        selectedDigestKey={selectedDigestKey}
      />
    ),
    onBackButtonPress: () => {
      navigate('/activities', { state: { skipAutoSelect: true } });
    },
    headerComponent: <ActivityFilters onFilterChange={handleFilterChange} />,
    disableScroll: true,
  };

  return (
    <ListCountProvider count={count}>
      <PanelShell
        leftColumn={leftColumn}
        // Publish the already-fetched digest rows to the outlet context so the
        // digest detail panel (`IntegrationDigestPanel`, rendered through the
        // activity <Outlet>) can read its per-asset breakdown without re-issuing
        // the namespaced `integration.syncDigests` query for a single row.
        outletContextExtras={{ digestRows }}
      />
    </ListCountProvider>
  );
};
